<template>
  <div data-role="message-user" class="message__user message__header">
    <div class="d-flex align-items-center">
      <div class="placeholder-col">
        <subscriber-avatar
          :subscriber="{ ...user, user_id: user.id }"
          small
          with-profile
        />
      </div>
      <div class="message__user--name">
        {{ username }}
        <div class="message-timestamp">
          {{ timestampAgo }}
        </div>
      </div>
    </div>

    <div class="d-flex align-items-center">
      <v-button
        v-if="isPinned"
        class="mr-n1 mt-n1"
        round
        ghost
        @click="showUnpinDialog"
      >
        <v-icon icon="push_pin" />
      </v-button>

      <v-button
        class="mr-n1 mt-n1"
        round
        ghost
        secondary
        @click="() => $emit('showActions')"
      >
        <v-icon icon="more_vert" />
      </v-button>
    </div>

    <alert-dialog
      :title="$t('components.messageThread.message.unpinDialogTitle')"
      :actions="[
        {
          title: $t('components.messageThread.message.unpinDialogCancel'),
          callback: () => (showUnpinConfirm = false)
        },
        {
          title: $t('components.messageThread.message.unpinDialogUnpin'),
          callback: handleUnpin
        }
      ]"
      :show="showUnpinConfirm"
      @close="() => (showUnpinConfirm = false)"
    >
      {{ $t("components.messageThread.message.unpinDialogContent") }}
    </alert-dialog>
  </div>
</template>

<script>
import { mapGetters, mapActions } from "vuex"

import { timeAgo } from "@/lib/date-utils"
import { canModerateMessage } from "@/lib/messages-utils"

import SubscriberAvatar from "@/components/SubscriberAvatar"

export default {
  components: {
    SubscriberAvatar,
    AlertDialog: () => import("@/components/AlertDialog")
  },

  props: {
    user: { type: Object, default: () => {} },
    message: { type: Object, required: true },
    ts: { type: [Number, String], default: null },
    username: { type: String, default: null }
  },

  data() {
    return {
      interval: null,
      timestampAgo: null,
      showUnpinConfirm: false
    }
  },

  computed: {
    ...mapGetters("auth", ["isAuthenticated", "currentUser"]),

    canPin() {
      return canModerateMessage(this.message, this.currentUser)
    },

    isPinned() {
      return !!this.message?.pinned_at
    }
  },

  mounted() {
    this.updateTimestampAgo()

    this.interval = setInterval(() => {
      this.updateTimestampAgo()
    }, 60000)
  },

  beforeDestroy() {
    clearInterval(this.interval)
  },

  methods: {
    ...mapActions("messages", ["pinPost"]),

    showUnpinDialog() {
      if (this.canPin) {
        this.showUnpinConfirm = true
      }
    },

    handleUnpin() {
      this.pinPost({
        isPinned: false,
        messageThreadId: this.message.message_thread_id,
        messageTs: this.message.message_ts
      })

      this.showUnpinConfirm = false

      this.$notify("Post Unpinned")
    },

    updateTimestampAgo() {
      this.timestampAgo = this.ts ? timeAgo(this.ts) : null
    }
  }
}
</script>

<style lang="scss" scoped>
@import "@/assets/styles/variables";

.message__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin: 0 0 $size--2;

  .placeholder-col {
    margin-right: $size--3;
  }

  .message__user--name {
    font-size: $font-size--small;
    font-weight: $font-weight--semi-bold;
    color: $color-ui--dark-blue;

    .message-timestamp {
      margin-top: 2px;
      font-size: $font-size--x-small;
      color: $color-ui--grey-70;
    }
  }
}
</style>
